/**
 * Small pieces the cashflow & Age Pension screens share (cashflow rebuild, 21 Oct 2026): the engine's own
 * working rendered as it comes, a number tile, the link and card classes.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import type { BreakdownRow } from './cashflowPensionScenario.ts';

export const link = 'font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy';
export const whyLink = 'font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900';
export const card = 'rounded-2xl border border-slate-200 bg-white p-6 shadow-card';
export const sec = 'text-[13px] font-semibold text-brand-navy';
export const money = (n: number) => formatCurrency(Math.round(n));
export const signed = (n: number) => `${n >= 0 ? '+' : '−'}${money(Math.abs(n))}`;
/** "short $2,742" or "$3,497 to spare". */
export const cashWords = (n: number) => (n < 0 ? `short ${money(-n)}` : `${money(n)} to spare`);

/**
 * The engine's own working, rendered as it comes.
 *
 * These arrive as an ORDERED ARRAY read top to bottom, not a keyed object — the first cut of this module
 * looked up keys like `assessableAssets`, every lookup missed, and the card would have shown a tidy column of
 * zeros with all the authority of a real calculation. Three row shapes are honoured: `--- Asset Test ---`
 * separators become headings, a row with an empty value is a note, and `value` is sometimes a formatted string
 * ("Part Pension") and sometimes a raw number.
 */
export const BreakdownList = ({ rows }: { rows?: BreakdownRow[] }) => {
    if (!rows?.length) {
        return <p className="text-slate-500">No working available for this figure.</p>;
    }
    return (
        <div className="space-y-0.5">
            {rows.map((row, i) => {
                const heading = /^-{2,}\s*(.+?)\s*-{2,}$/.exec(String(row.label));
                if (heading) {
                    return (
                        <p key={i} className="mt-2 border-t border-slate-200 pt-2 text-[12px] font-semibold text-slate-600 first:mt-0 first:border-0 first:pt-0">
                            {heading[1]}
                        </p>
                    );
                }
                if (row.value === '' || row.value == null) {
                    return <p key={i} className="text-slate-500">{row.label}</p>;
                }
                return (
                    <div key={i} className={`flex items-baseline justify-between gap-4 ${row.emphasis ? 'font-semibold text-slate-800' : ''}`}>
                        <span>{row.label}</span>
                        <span className="shrink-0 tabular-nums">{typeof row.value === 'number' ? formatCurrency(row.value) : row.value}</span>
                    </div>
                );
            })}
        </div>
    );
};

/** One of the four numbers. */
export const Tile = ({ label, value, unit, caption, tone, testId }: { label: string; value: string; unit?: string; caption: React.ReactNode; tone?: 'good' | 'bad'; testId?: string }) => (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-card" data-testid={testId}>
        <p className="text-[13px] font-semibold text-slate-500">{label}</p>
        <p className={`mt-1 text-[28px] font-bold leading-none tabular-nums tracking-tight ${tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-rose-700' : 'text-slate-900'}`}>
            {value}{unit && <span className="ml-1 text-[13px] font-semibold text-slate-500">{unit}</span>}
        </p>
        <p className="mt-2 text-[12.5px] leading-snug text-slate-500">{caption}</p>
    </div>
);

/** "why 10?" — the planning horizon, said once, in the same words everywhere on these screens. */
export const WHY_TEN_YEARS = 'Ten years is long enough to see what a choice does to what is left to the family and to the money each year, and short enough for the figures to still mean something. Every plan is tested over the same years, so they compare fairly. You can change it.';
